import { Injectable } from "@angular/core";
import { ConfigService } from "@merri/common";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import ApiEndpointsJson from "src/environments/configs/api.endpoints.json";
import AppConfigJson from "src/environments/configs/app.config.json";
import { environment } from "src/environments/environment";
import { AppConfig } from "../models/app-config";

@Injectable({
  providedIn: "root",
})
export class AppConfigService extends ConfigService {
  private appConfig: AppConfig;

  constructor() {
    super();
    ConfigService.production = environment.production;
  }

  get appVersion(): string {
    return "1.0.0";
  }

  get blobUrlBase(): string {
    return this.getConfig()?.baseBlobUrl;
  }

  get logSource(): string {
    return "Web.Catalog.Client";
  }

  getConfig(): AppConfig {
    return this.appConfig;
  }

  getManyGuidsUrl(count: number): string {
    return this.appConfig.getManyGuidsApiUrl.replace("{count}", count.toString());
  }

  getVendorUrl(vendorId?: number): string {
    return this.appConfig.vendorUrl.replace("{vendorId?}", (vendorId || "").toString());
  }

  postValidateVendorNameUrl(vendorId?: number): string {
    return this.appConfig.postValidateVendorNameUrl.replace("{vendorId?}", (vendorId || "").toString());
  }

  postVenueUrl(venueId?: number): string {
    return this.appConfig.postVenueApiUrl.replace("{venueId?}", (venueId || "").toString());
  }

  postValidateVenueNameUrl(venueId?: number): string {
    return this.appConfig.postValidateVenueNameApiUrl.replace("{venueId?}", (venueId || "").toString());
  }

  postVenueActiveUrl(venueId: number): string {
    return this.appConfig.postVenueActiveApiUrl.replace("{venueId}", venueId.toString());
  }

  getVenueUrl(venueId?: number): string {
    return this.appConfig.getVenueApiUrl.replace("{venueId?}", (venueId || "").toString());
  }

  getRoomUrl(venueId: number, roomId?: number): string {
    return this.appConfig.getRoomApiUrl.replace("{venueId}", venueId.toString()).replace("{roomId?}", (roomId || "").toString());
  }

  postRoomUrl(venueId: number, roomId?: number): string {
    return this.appConfig.postRoomApiUrl.replace("{venueId}", venueId.toString()).replace("{roomId?}", (roomId || "").toString());
  }

  postRoomActiveUrl(venueId: number, roomId: number): string {
    return this.appConfig.postRoomActiveApiUrl.replace("{venueId}", venueId.toString()).replace("{roomId}", roomId.toString());
  }

  uploadVenueProvidedItemsApiUrl(venueId: number): string {
    return this.appConfig.uploadVenueProvidedItemsApiUrl.replace("{venueId}", venueId.toString());
  }

  downloadVenueProvidedItemsApiUrl(venueId: number): string {
    return this.appConfig.downloadVenueProvidedItemsApiUrl.replace("{venueId}", venueId.toString());
  }

  getVenueProvidedItemsApiUrl(venueId: number): string {
    return this.appConfig.getVenueProvidedItemsApiUrl.replace("{venueId}", venueId.toString());
  }

  postVenueProvidedItemApiUrl(venueId: number, variantSkuId: number): string {
    return this.appConfig.postVenueProvidedItemApiUrl
      .replace("{venueId}", venueId.toString())
      .replace("{variantSkuId}", variantSkuId.toString());
  }

  getVenueEventPartnersApiUrl(venueId: number): string {
    return this.appConfig.getVenueEventPartnersApiUrl.replace("{venueId}", venueId.toString());
  }

  getVenueEventPartnerUsersApiUrl(venueId: number, partnerId: number): string {
    return this.appConfig.getVenueEventPartnerUsersApiUrl
      .replace("{venueId}", venueId.toString())
      .replace("{partnerId}", partnerId.toString());
  }

  postVenueEventPartnerUsersApiUrl(venueId: number, partnerId: number): string {
    return this.appConfig.postVenueEventPartnerUsersApiUrl
      .replace("{venueId}", venueId.toString())
      .replace("{partnerId}", partnerId.toString());
  }

  getVenueShowroomPartnersApiUrl(venueId: number): string {
    return this.appConfig.getVenueShowroomPartnersApiUrl.replace("{venueId}", venueId.toString());
  }

  getVenueShowroomPartnerUsersApiUrl(venueId: number, partnerId: number): string {
    return this.appConfig.getVenueShowroomPartnerUsersApiUrl
      .replace("{venueId}", venueId.toString())
      .replace("{partnerId}", partnerId.toString());
  }

  postVenueShowroomPartnerUsersApiUrl(venueId: number, partnerId: number): string {
    return this.appConfig.postVenueShowroomPartnerUsersApiUrl
      .replace("{venueId}", venueId.toString())
      .replace("{partnerId}", partnerId.toString());
  }

  getVariantSkusByAttributeValueUrl(attributeValue: string): string {
    return this.appConfig.getVariantSkusByAttributeValueUrl.replace("{attributeValue}", attributeValue);
  }

  getVariantSkusByTextUrl(text: string): string {
    return this.appConfig.getVariantSkusByTextUrl.replace("{text}", text);
  }

  getBlobSearchFilesUrl(container: string, keywords: string): string {
    return this.appConfig.getBlobSearchFilesApiUrl.replace("{container}", container).replace("{keywords}", keywords);
  }

  getVariantSkuByIdUrl(skuId: number): string {
    return this.appConfig.getVariantSkuByIdUrl.replace("{variantSkuId}", skuId.toString());
  }

  getVenuesAllUrl(partnerId: number): string {
    return this.appConfig.getVenuesAllApiUrl.concat(!!partnerId ? `?partnerId=${partnerId}` : "");
  }

  loadConfig(): Observable<boolean> {
    return of(true).pipe(
      map(() => {
        this.appConfig = this.buildConfig();

        return !!this.appConfig;
      }),
    );
  }

  private buildConfig(): AppConfig {
    const appConfig = new AppConfig();
    const baseApiUrl = AppConfigJson.baseApiUrl;

    appConfig.baseApiUrl = AppConfigJson.baseApiUrl;
    appConfig.baseBlobUrl = AppConfigJson.baseBlobUrl;
    appConfig.adClientId = AppConfigJson.adClientId;

    appConfig.merriHistoryApiUrl = baseApiUrl + ApiEndpointsJson.merriHistoryEndpoint;
    appConfig.merriTemplateApiUrl = baseApiUrl + ApiEndpointsJson.merriTemplateEndpoint;
    appConfig.merriUploadApiUrl = baseApiUrl + ApiEndpointsJson.merriUploadEndpoint;

    appConfig.vendorHistoryApiUrl = baseApiUrl + ApiEndpointsJson.vendorHistoryEndpoint;
    appConfig.vendorTemplateApiUrl = baseApiUrl + ApiEndpointsJson.vendorTemplateEndpoint;
    appConfig.vendorUploadApiUrl = baseApiUrl + ApiEndpointsJson.vendorUploadEndpoint;

    appConfig.modelHistoryApiUrl = baseApiUrl + ApiEndpointsJson.modelHistoryEndpoint;
    appConfig.modelTemplateApiUrl = baseApiUrl + ApiEndpointsJson.modelTemplateEndpoint;
    appConfig.modelUploadApiUrl = baseApiUrl + ApiEndpointsJson.modelUploadEndpoint;
    appConfig.modelDownloadAllApiUrl = baseApiUrl + ApiEndpointsJson.modelDownloadAllEndpoint;
    appConfig.modelUploadModelFilesUrl = baseApiUrl + ApiEndpointsJson.modelUploadModelFilesEndpoint;

    appConfig.materialHistoryApiUrl = baseApiUrl + ApiEndpointsJson.materialHistoryEndpoint;
    appConfig.materialTemplateApiUrl = baseApiUrl + ApiEndpointsJson.materialTemplateEndpoint;
    appConfig.materialUploadApiUrl = baseApiUrl + ApiEndpointsJson.materialUploadEndpoint;
    appConfig.materialDownloadAllApiUrl = baseApiUrl + ApiEndpointsJson.materialDownloadAllEndpoint;
    appConfig.materialUploadMaterialFilesUrl = baseApiUrl + ApiEndpointsJson.materialUploadMaterialFilesEndpoint;

    appConfig.getGuidApiUrl = baseApiUrl + ApiEndpointsJson.getGuidEndpoint;
    appConfig.getManyGuidsApiUrl = baseApiUrl + ApiEndpointsJson.getManyGuidsEndpoint;
    appConfig.getAllSubcategoriesUrl = baseApiUrl + ApiEndpointsJson.getAllSubcategoriesEndpoint;
    appConfig.variantSkusDownloadUrl = baseApiUrl + ApiEndpointsJson.variantSkusDownloadEndpoint;
    appConfig.skuImagesScriptDownloadUrl = baseApiUrl + ApiEndpointsJson.skuImagesScriptDownloadEndpoint;
    appConfig.skuImageFilesUploadUrl = baseApiUrl + ApiEndpointsJson.skuImageFilesUploadEndpoint;
    appConfig.getValidAttributeValuesUrl = baseApiUrl + ApiEndpointsJson.getValidAttributeValuesEndpoint;
    appConfig.getAttributeInfoUrl = baseApiUrl + ApiEndpointsJson.getAttributeInfoEndpoint;
    appConfig.getVariantSkusByAttributeValueUrl = baseApiUrl + ApiEndpointsJson.getVariantSkusByAttributeValueEndpoint;
    appConfig.postCreateValidAttributeValueUrl = baseApiUrl + ApiEndpointsJson.postCreateValidAttributeValueEndpoint;
    appConfig.postUpdateValidAttributeValueUrl = baseApiUrl + ApiEndpointsJson.postUpdateValidAttributeValueEndpoint;
    appConfig.getVariantSkusByTextUrl = baseApiUrl + ApiEndpointsJson.getVariantSkusByTextEndpoint;
    appConfig.postDiscontinueVendorSkuUrl = baseApiUrl + ApiEndpointsJson.postDiscontinueVendorSkuEndpoint;
    appConfig.getVariantSkuByIdUrl = baseApiUrl + ApiEndpointsJson.getVariantSkuByIdEndpoint;
    appConfig.postEditSingleVariantSkuUrl = baseApiUrl + ApiEndpointsJson.postEditSingleVariantSkuEndpoint;

    appConfig.getVendorsAllsUrl = baseApiUrl + ApiEndpointsJson.getVendorsAllEndpoint;
    appConfig.vendorUrl = baseApiUrl + ApiEndpointsJson.vendorEndpoint;
    appConfig.postValidateVendorNameUrl = baseApiUrl + ApiEndpointsJson.postValidateVendorNameEndpoint;

    appConfig.getVenuesAllApiUrl = baseApiUrl + ApiEndpointsJson.getVenuesAllEndpoint;
    appConfig.getVenueApiUrl = baseApiUrl + ApiEndpointsJson.getVenueEndpoint;
    appConfig.postVenueApiUrl = baseApiUrl + ApiEndpointsJson.postVenueEndpoint;
    appConfig.postValidateVenueNameApiUrl = baseApiUrl + ApiEndpointsJson.postValidateVenueNameEndpoint;
    appConfig.postVenueActiveApiUrl = baseApiUrl + ApiEndpointsJson.postVenueActiveEndpoint;
    appConfig.getRoomApiUrl = baseApiUrl + ApiEndpointsJson.getRoomEndpoint;
    appConfig.postRoomApiUrl = baseApiUrl + ApiEndpointsJson.postRoomEndpoint;
    appConfig.postRoomActiveApiUrl = baseApiUrl + ApiEndpointsJson.postRoomActiveEndpoint;
    appConfig.postValidatePartnerApiUrl = baseApiUrl + ApiEndpointsJson.postValidatePartnerEndpoint;

    appConfig.postDownloadMerriSkusApiUrl = baseApiUrl + ApiEndpointsJson.postDownloadMerriSkusEndpoint;
    appConfig.postDownloadVariantSkusApiUrl = baseApiUrl + ApiEndpointsJson.postDownloadVariantSkusEndpoint;

    appConfig.uploadVenueProvidedItemsApiUrl = baseApiUrl + ApiEndpointsJson.uploadVenueProvidedItemsEndpoint;
    appConfig.downloadVenueProvidedItemsApiUrl = baseApiUrl + ApiEndpointsJson.downloadVenueProvidedItemsEndpoint;
    appConfig.getVenueProvidedItemsApiUrl = baseApiUrl + ApiEndpointsJson.getVenueProvidedItemsEndpoint;
    appConfig.postVenueProvidedItemApiUrl = baseApiUrl + ApiEndpointsJson.postVenueProvidedItemEndpoint;
    appConfig.getVenueEventPartnersApiUrl = baseApiUrl + ApiEndpointsJson.getVenueEventPartnersEndpoint;
    appConfig.getVenueEventPartnerUsersApiUrl = baseApiUrl + ApiEndpointsJson.getVenueEventPartnerUsersEndpoint;
    appConfig.postVenueEventPartnerUsersApiUrl = baseApiUrl + ApiEndpointsJson.postVenueEventPartnerUsersEndpoint;
    appConfig.getVenueShowroomPartnersApiUrl = baseApiUrl + ApiEndpointsJson.getVenueShowroomPartnersEndpoint;
    appConfig.getVenueShowroomPartnerUsersApiUrl = baseApiUrl + ApiEndpointsJson.getVenueShowroomPartnerUsersEndpoint;
    appConfig.postVenueShowroomPartnerUsersApiUrl = baseApiUrl + ApiEndpointsJson.postVenueShowroomPartnerUsersEndpoint;

    appConfig.getBlobContainersApiUrl = baseApiUrl + ApiEndpointsJson.getBlobContainersEndpoint;
    appConfig.getBlobSearchFilesApiUrl = baseApiUrl + ApiEndpointsJson.getBlobSearchFilesEndpoint;

    appConfig.customRoomPropsApiUrl = baseApiUrl + ApiEndpointsJson.customRoomPropsEndpoint;
    appConfig.customRoomPropsTemplateApiUrl = baseApiUrl + ApiEndpointsJson.customRoomPropsTemplateEndpoint;
    appConfig.customRoomPropsUploadApiUrl = baseApiUrl + ApiEndpointsJson.customRoomPropsUploadEndpoint;
    appConfig.customRoomPropsImagesUploadApiUrl = baseApiUrl + ApiEndpointsJson.customRoomPropsImagesUploadEndpoint;
    appConfig.customRoomPropsModelsUploadApiUrl = baseApiUrl + ApiEndpointsJson.customRoomPropsModelsUploadEndpoint;
    appConfig.customRoomTexturesApiUrl = baseApiUrl + ApiEndpointsJson.customRoomTexturesEndpoint;
    appConfig.customRoomTexturesTemplateApiUrl = baseApiUrl + ApiEndpointsJson.customRoomTexturesTemplateEndpoint;
    appConfig.customRoomTexturesUploadApiUrl = baseApiUrl + ApiEndpointsJson.customRoomTexturesUploadEndpoint;
    appConfig.customRoomTexturesImagesUploadApiUrl = baseApiUrl + ApiEndpointsJson.customRoomTexturesImagesUploadEndpoint;
    appConfig.customRoomTexturesMaterialsUploadApiUrl = baseApiUrl + ApiEndpointsJson.customRoomTexturesMaterialsUploadEndpoint;

    return appConfig;
  }
}
